import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { 
  BookOpen, 
  GraduationCap, 
  Globe, 
  Sparkles, 
  Layers, 
  CheckCircle2, 
  Clock, 
  ChevronDown,
  Atom,
  Calculator,
  FlaskConical,
  Dna,
  Cpu,
  Code2,
  TrendingUp,
  LineChart,
  Brain,
  Compass,
  FileText,
  UploadCloud,
  ArrowRight,
  BookMarked,
  RefreshCw,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { StudyMaterialsSection } from './StudyMaterialsSection';
import { UploadedMaterial, StudyMaterialAnalysis, ExamPrepOptions } from '@/lib/studyMaterials';
import { fetchSuggestedChapters, getDefaultSyllabusChapters } from '@/lib/chapterSuggestions';

export interface OneShotSubmitParams {
  subject: string;
  classLevel: string;
  board: string;
  chapterTopic: string;
  language: string;
  studyMaterialsContext?: {
    materials: UploadedMaterial[];
    analysis: StudyMaterialAnalysis;
    options: ExamPrepOptions;
  };
}

interface OneShotInputSectionProps {
  onSubmit: (params: OneShotSubmitParams) => void;
  isLoading: boolean;
}

const SUBJECT_OPTIONS = [
  { id: 'Mathematics', label: 'Mathematics', category: 'STEM', icon: Calculator },
  { id: 'Physics', label: 'Physics', category: 'STEM', icon: Atom },
  { id: 'Chemistry', label: 'Chemistry', category: 'STEM', icon: FlaskConical },
  { id: 'Biology', label: 'Biology', category: 'STEM', icon: Dna },
  { id: 'Computer Science', label: 'Computer Science', category: 'Tech', icon: Cpu },
  { id: 'Programming', label: 'Programming & Software Eng.', category: 'Tech', icon: Code2 },
  { id: 'Engineering', label: 'Engineering & Technology', category: 'Tech', icon: Layers },
  { id: 'Economics', label: 'Economics & Commerce', category: 'Social Science', icon: TrendingUp },
  { id: 'Statistics', label: 'Statistics & Data Science', category: 'STEM', icon: LineChart },
  { id: 'Logical Reasoning', label: 'Logical Reasoning & Aptitude', category: 'Aptitude', icon: Brain },
  { id: 'History & Civics', label: 'History & Civics', category: 'Humanities', icon: BookMarked },
  { id: 'Geography', label: 'Geography & Environment', category: 'Humanities', icon: Compass },
  { id: 'Other', label: 'Other / Custom Subject', category: 'General', icon: Compass }
];

const CLASS_LEVELS = [
  'Class 6',
  'Class 7',
  'Class 8',
  'Class 9',
  'Class 10',
  'Class 11',
  'Class 12',
  'Undergraduate / College',
  'Competitive Exam / General'
];

const BOARDS = [
  'CBSE',
  'ICSE',
  'State Board',
  'IB / IGCSE',
  'General / University'
];

const LANGUAGES = [
  "English", "Hindi", "Bengali", "Gujarati", "Kannada", "Malayalam", "Marathi", "Odia", "Punjabi", "Tamil", "Telugu", 
  "Spanish", "French", "German", "Mandarin", "Japanese", "Korean", 
  "Arabic", "Portuguese", "Russian", "Italian"
];

export function OneShotInputSection({ onSubmit, isLoading }: OneShotInputSectionProps) {
  const [learningMode, setLearningMode] = useState<'standard' | 'materials'>('standard');
  const [selectedSubject, setSelectedSubject] = useState<string>('Mathematics');
  const [customSubject, setCustomSubject] = useState<string>('');
  const [classLevel, setClassLevel] = useState<string>('Class 10');
  const [board, setBoard] = useState<string>('CBSE');
  const [chapterTopic, setChapterTopic] = useState<string>('');
  const [language, setLanguage] = useState<string>('English');

  const [aiSuggestions, setAiSuggestions] = useState<string[]>(() => 
    getDefaultSyllabusChapters('Mathematics', 'Class 10', 'CBSE')
  );
  const [isFetchingChapters, setIsFetchingChapters] = useState<boolean>(false);

  const activeSubject = selectedSubject === 'Other' ? (customSubject.trim() || 'General Subject') : selectedSubject;

  // Auto-fetch dynamic AI curriculum chapter suggestions whenever subject, level, or board changes
  useEffect(() => {
    let isMounted = true;
    if (!activeSubject) return;

    // Immediately set accurate syllabus default chapters for the newly selected grade and subject
    const defaultChapters = getDefaultSyllabusChapters(activeSubject, classLevel, board);
    setAiSuggestions(defaultChapters);

    const loadSuggestions = async () => {
      setIsFetchingChapters(true);
      try {
        const fetched = await fetchSuggestedChapters(activeSubject, classLevel, board);
        if (isMounted) {
          if (fetched && fetched.length > 0) {
            setAiSuggestions(fetched);
          } else {
            setAiSuggestions(defaultChapters);
          }
        }
      } catch (err) {
        console.warn("Failed to fetch chapter suggestions:", err);
        if (isMounted) {
          setAiSuggestions(defaultChapters);
        }
      } finally {
        if (isMounted) setIsFetchingChapters(false);
      }
    };

    loadSuggestions();

    return () => { isMounted = false; };
  }, [selectedSubject, customSubject, classLevel, board]);

  const handleRefreshSuggestions = async () => {
    setIsFetchingChapters(true);
    try {
      const fetched = await fetchSuggestedChapters(activeSubject, classLevel, board);
      if (fetched && fetched.length > 0) {
        setAiSuggestions(fetched);
      }
    } catch (e) {
      console.warn("Refresh failed", e);
    } finally {
      setIsFetchingChapters(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!chapterTopic.trim()) return;
    onSubmit({
      subject: activeSubject,
      classLevel,
      board,
      chapterTopic: chapterTopic.trim(),
      language
    });
  };

  const currentSuggestions = aiSuggestions.length > 0 
    ? aiSuggestions 
    : getDefaultSyllabusChapters(activeSubject, classLevel, board);

  return (
    <div className="w-full max-w-4xl mx-auto bg-slate-900/90 backdrop-blur-xl rounded-3xl shadow-2xl border border-white/15 overflow-hidden">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-indigo-950 via-slate-900 to-indigo-950 px-6 py-5 text-white flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/10">
        <div className="flex items-center gap-3.5">
          <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500/30 to-purple-500/20 border border-indigo-400/40 flex items-center justify-center text-indigo-300 shrink-0 shadow-lg">
            <GraduationCap className="w-6 h-6 text-indigo-300" />
          </div>
          <div>
            <h2 className="text-lg font-extrabold tracking-tight flex items-center gap-2 text-white">
              OneShot Learning Masterclass
              <span className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-[10px] uppercase font-black px-2.5 py-0.5 rounded-full tracking-wider">
                20 Min Fixed Format
              </span>
            </h2>
            <p className="text-xs text-slate-300 mt-0.5">
              Master an entire chapter from scratch • 15 Min Concept Teaching + 5 Min Exam Practice
            </p>
          </div>
        </div>

        {/* Mode Switcher Tabs */}
        <div className="flex items-center bg-slate-950/80 p-1.5 rounded-2xl border border-white/10 shrink-0">
          <button
            type="button"
            onClick={() => setLearningMode('standard')}
            className={cn(
              "px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer",
              learningMode === 'standard'
                ? "bg-indigo-600 text-white shadow-lg shadow-indigo-500/30"
                : "text-slate-400 hover:text-white hover:bg-white/5"
            )}
          >
            <BookOpen className="w-3.5 h-3.5" />
            <span>Standard Topic Mode</span>
          </button>

          <button
            type="button"
            onClick={() => setLearningMode('materials')}
            className={cn(
              "px-4 py-2 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer",
              learningMode === 'materials'
                ? "bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-950 shadow-lg shadow-emerald-500/30 font-extrabold"
                : "text-emerald-400 hover:text-emerald-300 hover:bg-white/5"
            )}
          >
            <UploadCloud className="w-3.5 h-3.5" />
            <span>Study From Your Materials</span>
          </button>
        </div>
      </div>

      {learningMode === 'materials' ? (
        <div className="p-6 bg-slate-950/60">
          <StudyMaterialsSection onSubmit={onSubmit} isLoading={isLoading} />
        </div>
      ) : (
      <form onSubmit={handleSubmit} className="p-6 sm:p-7 space-y-6 bg-slate-950/70">
        {/* Step 1 to 4 Grid Configuration Controls */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
          
          {/* Step 1: Dropdown Subject Selection */}
          <div className="space-y-1.5 bg-slate-900/90 p-3 rounded-2xl border border-white/10 hover:border-indigo-500/40 transition-colors">
            <label className="text-[11px] font-bold text-indigo-300 uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[10px] font-black flex items-center justify-center">1</span>
              Subject
            </label>
            <div className="relative">
              <select
                value={selectedSubject}
                onChange={(e) => setSelectedSubject(e.target.value)}
                disabled={isLoading}
                className="w-full bg-slate-800/90 hover:bg-slate-800 border border-white/10 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 rounded-xl px-3 py-2 text-xs font-bold text-white outline-none cursor-pointer transition-all appearance-none pr-8"
              >
                {SUBJECT_OPTIONS.map((sub) => (
                  <option key={sub.id} value={sub.id} className="bg-slate-900 text-white">
                    {sub.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Step 2: Class Level */}
          <div className="space-y-1.5 bg-slate-900/90 p-3 rounded-2xl border border-white/10 hover:border-indigo-500/40 transition-colors">
            <label className="text-[11px] font-bold text-indigo-300 uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[10px] font-black flex items-center justify-center">2</span>
              Academic Level
            </label>
            <div className="relative">
              <select
                value={classLevel}
                onChange={(e) => setClassLevel(e.target.value)}
                disabled={isLoading}
                className="w-full bg-slate-800/90 hover:bg-slate-800 border border-white/10 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 rounded-xl px-3 py-2 text-xs font-bold text-white outline-none cursor-pointer transition-all appearance-none pr-8"
              >
                {CLASS_LEVELS.map((cls) => (
                  <option key={cls} value={cls} className="bg-slate-900 text-white">{cls}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Step 3: Board / Curriculum */}
          <div className="space-y-1.5 bg-slate-900/90 p-3 rounded-2xl border border-white/10 hover:border-indigo-500/40 transition-colors">
            <label className="text-[11px] font-bold text-indigo-300 uppercase tracking-wider flex items-center gap-1.5">
              <span className="w-4 h-4 rounded-full bg-indigo-500 text-white text-[10px] font-black flex items-center justify-center">3</span>
              Curriculum
            </label>
            <div className="relative">
              <select
                value={board}
                onChange={(e) => setBoard(e.target.value)}
                disabled={isLoading}
                className="w-full bg-slate-800/90 hover:bg-slate-800 border border-white/10 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 rounded-xl px-3 py-2 text-xs font-bold text-white outline-none cursor-pointer transition-all appearance-none pr-8"
              >
                {BOARDS.map((b) => (
                  <option key={b} value={b} className="bg-slate-900 text-white">{b}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

          {/* Step 4: Language */}
          <div className="space-y-1.5 bg-slate-900/90 p-3 rounded-2xl border border-white/10 hover:border-indigo-500/40 transition-colors">
            <label className="text-[11px] font-bold text-indigo-300 uppercase tracking-wider flex items-center gap-1.5">
              <Globe className="w-3.5 h-3.5 text-indigo-400" />
              Language
            </label>
            <div className="relative">
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                disabled={isLoading}
                className="w-full bg-slate-800/90 hover:bg-slate-800 border border-white/10 focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20 rounded-xl px-3 py-2 text-xs font-bold text-white outline-none cursor-pointer transition-all appearance-none pr-8"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang} className="bg-slate-900 text-white">{lang}</option>
                ))}
              </select>
              <ChevronDown className="w-4 h-4 text-slate-400 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>

        </div>

        {/* Custom Subject Field if "Other" is selected */}
        {selectedSubject === 'Other' && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
            <input
              type="text"
              placeholder="Enter your custom subject name (e.g. Astronomy, Financial Accounting, World History...)"
              value={customSubject}
              onChange={(e) => setCustomSubject(e.target.value)}
              disabled={isLoading}
              className="w-full bg-slate-800/90 border border-indigo-400/30 rounded-xl px-4 py-2.5 text-xs font-bold text-white placeholder:text-slate-400 focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-500/20"
            />
          </motion.div>
        )}

        {/* Chapter / Topic Input Area */}
        <div className="space-y-3 bg-slate-900/90 p-4 rounded-2xl border border-white/10">
          <label className="text-xs font-bold text-slate-200 uppercase tracking-wider flex items-center justify-between">
            <span className="flex items-center gap-2 text-indigo-300">
              <BookOpen className="w-4 h-4 text-emerald-400" />
              Chapter Name or Specific Topic
            </span>
            <span className="text-[11px] text-slate-400 font-medium normal-case">
              Enter any chapter from your syllabus
            </span>
          </label>

          <div className="relative">
            <input
              type="text"
              required
              placeholder={`Enter chapter name (e.g. ${currentSuggestions[0] || 'Quadratic Equations'})`}
              value={chapterTopic}
              onChange={(e) => setChapterTopic(e.target.value)}
              disabled={isLoading}
              className="w-full bg-slate-950/90 border border-white/15 focus:border-emerald-400 focus:ring-2 focus:ring-emerald-500/20 rounded-2xl px-5 py-4 text-sm font-bold text-white placeholder:text-slate-500 outline-none shadow-inner transition-all pr-12"
            />
            <Sparkles className="w-5 h-5 text-emerald-400 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" />
          </div>

          {/* AI Auto-Suggested Chapter Chips */}
          <div className="space-y-2 pt-1">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1.5">
                <Sparkles className="w-3.5 h-3.5 text-amber-400" />
                Auto-Suggested Chapters ({board} • {classLevel} • {activeSubject}):
              </span>

              <button
                type="button"
                onClick={handleRefreshSuggestions}
                disabled={isFetchingChapters || isLoading}
                title="Refresh suggested chapters"
                className="text-[10px] font-bold text-indigo-300 hover:text-white flex items-center gap-1 bg-indigo-950 hover:bg-indigo-900/80 px-2.5 py-1 rounded-lg border border-indigo-700/50 transition-colors cursor-pointer"
              >
                <RefreshCw className={cn("w-3 h-3", isFetchingChapters && "animate-spin text-indigo-400")} />
                <span>Refresh Suggestions</span>
              </button>
            </div>

            {isFetchingChapters ? (
              <div className="flex items-center gap-2 py-1 text-xs text-indigo-300 font-medium">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-indigo-400" />
                <span>Fetching curriculum-accurate chapters for {board} {classLevel}...</span>
              </div>
            ) : currentSuggestions.length > 0 ? (
              <div className="flex items-center gap-2 flex-wrap">
                {currentSuggestions.map((topic, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setChapterTopic(topic)}
                    className={cn(
                      "text-[11px] font-semibold px-3 py-1.5 rounded-xl transition-all cursor-pointer border shadow-xs text-left",
                      chapterTopic === topic
                        ? "bg-emerald-500 text-slate-950 border-emerald-400 font-extrabold shadow-md shadow-emerald-500/20"
                        : "bg-slate-800/90 hover:bg-slate-800 text-slate-200 border-white/10 hover:border-emerald-500/50 hover:text-emerald-300"
                    )}
                  >
                    + {topic}
                  </button>
                ))}
              </div>
            ) : null}
          </div>
        </div>

        {/* Submit Action Button */}
        <div className="pt-2 flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2 text-xs text-slate-300 font-medium">
            <Clock className="w-4 h-4 text-amber-400 shrink-0" />
            <span>Generates 20-min modulewise video lesson with comprehensive study notes</span>
          </div>

          <button
            type="submit"
            disabled={isLoading || !chapterTopic.trim()}
            className="w-full sm:w-auto ml-auto flex items-center justify-center gap-2.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-indigo-600 hover:from-emerald-400 hover:to-indigo-500 text-slate-950 font-montserrat font-extrabold px-8 py-3.5 rounded-2xl text-sm transition-all shadow-xl shadow-emerald-500/20 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            <Sparkles className="w-4.5 h-4.5 text-slate-950 animate-pulse" />
            <span>Start OneShot Masterclass</span>
            <ArrowRight className="w-4 h-4 text-slate-950" />
          </button>
        </div>
      </form>
      )}
    </div>
  );
}

